/**
 * LookupTableEditor.tsx — editable grid (columns + rows) for a calc's lookup
 * tables. Each table persists via the lookup-table CRUD endpoints. Prices are
 * never allowed as columns (server rejects; we also block client-side).
 */

import { useState } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import type { LookupTable } from '../../../api/calculatorBuilder';
import { StudioPanel, field, btn, btnAccent, label, btnGhostDanger } from './ui';

const FORBIDDEN_COLS = new Set(['price', 'priceinr', 'unitprice', 'capexinr', 'amount_inr']);

interface Props {
  tables: LookupTable[];
  /** save/upsert one table (POST or PUT). */
  onSaveTable: (table: LookupTable) => Promise<void> | void;
  onDeleteTable: (tableKey: string) => Promise<void> | void;
  /** local mutation of the working set (before save). */
  onChange: (tables: LookupTable[]) => void;
}

export default function LookupTableEditor({ tables, onSaveTable, onDeleteTable, onChange }: Props) {
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const patch = (ti: number, p: Partial<LookupTable>) => onChange(tables.map((t, idx) => (idx === ti ? { ...t, ...p } : t)));

  const addTable = () =>
    onChange([...tables, { tableKey: `table_${tables.length + 1}`, columns: ['key', 'value'], rows: [], mode: 'exact' }]);

  const addCol = (ti: number) => patch(ti, { columns: [...tables[ti].columns, `col_${tables[ti].columns.length + 1}`] });
  const setCol = (ti: number, ci: number, v: string) =>
    patch(ti, { columns: tables[ti].columns.map((c, idx) => (idx === ci ? v : c)) });
  const removeCol = (ti: number, ci: number) =>
    patch(ti, {
      columns: tables[ti].columns.filter((_, idx) => idx !== ci),
      rows: tables[ti].rows.map((r) => r.filter((_, idx) => idx !== ci)),
    });

  const addRow = (ti: number) => patch(ti, { rows: [...tables[ti].rows, tables[ti].columns.map(() => '')] });
  const setCell = (ti: number, ri: number, ci: number, v: string) =>
    patch(ti, {
      rows: tables[ti].rows.map((r, idx) => (idx === ri ? r.map((cell, cidx) => (cidx === ci ? coerce(v) : cell)) : r)),
    });
  const removeRow = (ti: number, ri: number) => patch(ti, { rows: tables[ti].rows.filter((_, idx) => idx !== ri) });

  const priceColHit = (t: LookupTable) => t.columns.find((c) => FORBIDDEN_COLS.has(c.toLowerCase()));

  const save = async (t: LookupTable) => {
    setSavingKey(t.tableKey);
    try {
      await onSaveTable(t);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <StudioPanel
      title="Lookup tables"
      subtitle="Key/value grids referenced by lookup() formulas. No price columns — prices live in Product.unitPrice."
      right={
        <button type="button" style={btnAccent} onClick={addTable}>
          <Plus size={14} /> Add table
        </button>
      }
    >
      {tables.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-3, #6b7280)' }}>No lookup tables.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {tables.map((t, ti) => {
          const badCol = priceColHit(t);
          return (
            <div key={ti} style={{ border: '1px solid var(--line, #e5e7eb)', borderRadius: 9, padding: 12 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
                <div>
                  <span style={label}>Table key</span>
                  <input
                    style={{ ...field, fontFamily: 'ui-monospace, monospace' }}
                    value={t.tableKey}
                    onChange={(e) => patch(ti, { tableKey: e.target.value.trim() })}
                  />
                </div>
                <div>
                  <span style={label}>Mode</span>
                  <select style={field} value={t.mode ?? 'exact'} onChange={(e) => patch(ti, { mode: e.target.value as LookupTable['mode'] })}>
                    <option value="exact">exact</option>
                    <option value="nearest">nearest</option>
                    <option value="ceil">ceil</option>
                  </select>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    style={btnAccent}
                    disabled={!!badCol || savingKey === t.tableKey}
                    onClick={() => save(t)}
                  >
                    <Save size={14} /> {savingKey === t.tableKey ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" style={btnGhostDanger} onClick={() => onDeleteTable(t.tableKey)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {badCol && (
                <div style={{ fontSize: 12, color: 'var(--danger, #dc2626)', marginBottom: 8 }}>
                  Column “{badCol}” looks like a price — not allowed. Prices live in Product.unitPrice.
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      {t.columns.map((c, ci) => (
                        <th key={ci} style={{ padding: 4, borderBottom: '1px solid var(--line, #e5e7eb)' }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input
                              style={{ ...field, height: 26, fontFamily: 'ui-monospace, monospace' }}
                              value={c}
                              onChange={(e) => setCol(ti, ci, e.target.value.trim())}
                            />
                            <button type="button" style={{ ...btn, height: 26, padding: '0 6px' }} onClick={() => removeCol(ti, ci)}>
                              ×
                            </button>
                          </div>
                        </th>
                      ))}
                      <th style={{ padding: 4 }}>
                        <button type="button" style={{ ...btn, height: 26, padding: '0 8px' }} onClick={() => addCol(ti)}>
                          + col
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.rows.map((row, ri) => (
                      <tr key={ri}>
                        {t.columns.map((_, ci) => (
                          <td key={ci} style={{ padding: 3 }}>
                            <input
                              style={{ ...field, height: 28, width: '100%' }}
                              value={String(row[ci] ?? '')}
                              onChange={(e) => setCell(ti, ri, ci, e.target.value)}
                            />
                          </td>
                        ))}
                        <td style={{ padding: 3 }}>
                          <button type="button" style={btnGhostDanger} onClick={() => removeRow(ti, ri)}>
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => addRow(ti)}>
                <Plus size={14} /> Add row
              </button>
            </div>
          );
        })}
      </div>
    </StudioPanel>
  );
}

/** Coerce a cell string to a number when it looks numeric (keeps string enums as-is). */
function coerce(v: string): string | number {
  if (v.trim() === '') return '';
  const n = Number(v);
  return Number.isFinite(n) && String(n) === v.trim() ? n : v;
}
